import React, {PureComponent} from 'react';
import DocumentTitle from 'react-document-title';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import isFunction from 'lodash/isFunction';
import {toast} from 'react-toastify';
import Game from '../../components/Game';
import LeaderboardPanel from '../../components/LeaderboardPanel';
import TreasureChestPanel from '../../components/TreasureChestPanel';
import {Chat, Wrapper} from '../../components/common';
import * as appActions from '../../actions/application';
import * as userActions from '../../actions/user';
import * as playerActions from '../../actions/player';
import * as gameActions from '../../actions/game';
import * as panelActions from '../../actions/panelNav';

import {
  ABLY_EVENT_PLAYER_HAS_JOINED,
  ABLY_EVENT_PLAYER_HAS_LEFT,
  ABLY_EVENT_PLAYER_READY,
  ABLY_EVENT_GAME_HAS_STARTED,
  ABLY_EVENT_GAME_ROUND_HAS_FINISHED,
  ABLY_EVENT_GAME_ROUND_HAS_STARTED,
  ABLY_EVENT_GAME_QUESTION_NEXT,
  ABLY_GAME_ADMIN_HAS_CHANGED,
  ABLY_EVENT_PLAYER_ANSWER_HAS_CHECKED,
  ABLY_EVENT_PLAYER_ANSWER_HAS_SELECTED,
  ABLY_EVENT_GAME_HAS_FINISHED,
  ABLY_EVENT_GAME_INTERSTITIAL_HAS_STARTED,
  ABLY_EVENT_GAME_INTERSTITIAL_HAS_FINISHED,
  ABLY_EVENT_PLAYER_SEND_GIFT_SUCCESS,
  ABLY_EVENT_GAME_TEAM_POINTS_INCREMENT,
  ABLY_EVENT_GAME_PLAYER_POINTS_INCREMENT,
  ABLY_EVENT_GAME_PLAYER_KICK_INIT,
  ABLY_EVENT_GAME_PLAYER_KICK_VOTED,
  ABLY_EVENT_GAME_PLAYER_KICKED,
  ABLY_EVENT_GAME_RESPONDENT_HAS_BEEN_CHANGED,
  CHAT_EVENT,
  ABLY_ERROR,
} from '../../configs/constants';
import {ON_API_ERROR} from '../../actions/types';
import AblyService from '../../utils/ably';
import VideoServiceNG from '../../utils/videoServiceNg';
import {gameConfigSelector, templateConfigSelector} from '../../selectors';
import correctSound from '../../assets/correct.mp3';
import wrongSound from '../../assets/wrong.mp3';
import JoinGameByDirectLink from '../../components/Lobby/GamesList/JoinGameByDirectLinkModal/index';
import {treasureRedeemedMessage} from '../../configs/messages';
import {redeemClean} from '../../actions/treasures';

class GameContainer extends PureComponent {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        gameId: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    onPlayerJoinMessage: PropTypes.func.isRequired,
    onPlayerLeft: PropTypes.func.isRequired,
    onPlayerReadyMessage: PropTypes.func.isRequired,
    confirmMedia: PropTypes.func.isRequired,
    onGameStart: PropTypes.func.isRequired,
    onPlayerJoin: PropTypes.func.isRequired,
    onPlayerLeave: PropTypes.func.isRequired,
    // setSidebarStatus: PropTypes.func.isRequired,
    onGameRoundFinished: PropTypes.func.isRequired,
    onGameRoundStarted: PropTypes.func.isRequired,
    onGameQuestionNextMessage: PropTypes.func.isRequired,
    onGameAdminChanged: PropTypes.func.isRequired,
    onPlayerAnswerHasSelected: PropTypes.func.isRequired,
    onPlayerAnswerHasChecked: PropTypes.func.isRequired,
    onGameHasFinished: PropTypes.func.isRequired,
    onPlayerOnline: PropTypes.func.isRequired,
    onPlayerOffline: PropTypes.func.isRequired,
    onInterstitialHasStarted: PropTypes.func.isRequired,
    onInterstitialHasFinished: PropTypes.func.isRequired,
    fetchTeamDefinition: PropTypes.func.isRequired,
    onPlayerGiftSent: PropTypes.func.isRequired,
    onTeamGetsPoints: PropTypes.func.isRequired,
    onPlayerGetsPoints: PropTypes.func.isRequired,
    onPlayerEnteredGameByDirectLink: PropTypes.func.isRequired,
    onPlayerKickInit: PropTypes.func.isRequired,
    onPlayerKickVoted: PropTypes.func.isRequired,
    onPlayerKicked: PropTypes.func.isRequired,
    onPlayerKickCancel: PropTypes.func.isRequired,
    onRespondentChanged: PropTypes.func.isRequired,
    onRemoteVideoMuted: PropTypes.func.isRequired,
    onRemoteVideoUnmuted: PropTypes.func.isRequired,
    onPlayerRejoined: PropTypes.func.isRequired,
    toggleLeaderboard: PropTypes.func.isRequired,
    toggleTreasureChest: PropTypes.func.isRequired,
    toggleChatPanel: PropTypes.func.isRequired,
    application: PropTypes.shape().isRequired,
    panel: PropTypes.shape().isRequired,
    user: PropTypes.shape().isRequired,
    game: PropTypes.shape().isRequired,
    saveUserDefinitions: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    gameConfig: PropTypes.shape({
      isAudienceAllowed: PropTypes.bool,
    }).isRequired,
    templateConfig: PropTypes.shape({
      primaryColor: PropTypes.string.isRequired,
      secondaryColor: PropTypes.string.isRequired,
    }).isRequired,
    error: PropTypes.shape({}).isRequired,
    fetchGameTeamDefinition: PropTypes.func.isRequired,
    fetchMobileBranchIoLink: PropTypes.func.isRequired,
    ableToSendGiftsForFree: PropTypes.bool.isRequired,
    treasures: PropTypes.shape({
      redeemSuccess: PropTypes.bool.isRequired,
    }).isRequired,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const {game: {currentGame: {state: gameState}}} = nextProps;

    // Show the join form until the game has a phase, and recompute only when
    // that phase actually changes. (The previous guard compared `prevState.game`,
    // a key this component never stores, so the condition was always true and it
    // recomputed on every render.)
    if (gameState === prevState.gameState) {
      return null;
    }

    return {gameState, displayJoinForm: !gameState};
  }

  constructor(props) {
    super(props);
    const {
      match: {params: {gameId}},
      game: {currentGame: {state}},
    } = props;

    this.state = {
      subscribedStreams: {},
      streamId: null,
      gameState: state,
      displayJoinForm: !state,
    };

    this.gameId = gameId;
    this.videoServiceNg = this.videoServiceNg || new VideoServiceNG();
    this.correctSound = new Audio(correctSound);
    this.wrongSound = new Audio(wrongSound);
  }

  componentDidMount() {
    const {displayJoinForm} = this.state;

    if (AblyService.realtime) {
      this.channel = AblyService.realtime.channels.get(`game/${this.gameId}`);
      this.chatChannel = AblyService.realtime.channels.get(`game/${this.gameId}/chat`);
    }

    this.getPlayersOnline();

    if (!displayJoinForm) {
      this.initVideoService();
      this.subscribeEvents();
    }

    this.redeemToast();

    const {panel: {isChatPanelOpened}} = this.props;
    if (window.innerWidth < 700 && isChatPanelOpened) {
      this.onChatToggle(false);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.displayJoinForm !== this.state.displayJoinForm) {
      this.initVideoService();
      this.subscribeEvents();
    }

    const {game: {currentGame: {playerKickError: currentError}}} = this.props;
    const {game: {currentGame: {playerKickError: prevError}}} = prevProps;

    if (!prevError && currentError) {
      toast.error(currentError);
    }

    this.redeemToast();
  }

  componentWillUnmount() {
    if (this.channel) {
      // ably v2: presence.leave() returns a Promise; fire-and-forget with a
      // catch so a rejected leave never surfaces as an unhandled rejection.
      this.channel.presence.leave().catch(err => console.error('Error leaving presence:', err));
      this.channel.unsubscribe();
      this.channel.presence.unsubscribe();
    }

    if (this.chatChannel) {
      this.chatChannel.unsubscribe();
    }

    if (this.videoServiceNg) {
      this.videoServiceNg.stop();
    }
  }

  reInitializeVideo = async () => {
    const {user: {profile: {id: userId}}} = this.props;
    try {
      await this.videoServiceNg.stop();
      await this.videoServiceNg.joinGame(userId, this.gameId);
      this.onVideoClientReJoined();
    } catch (e) {
      console.error(e);
    }
  };

  onAnswerSelected = (event) => {
    const message = {answerId: event.target.value};

    this.channel.publish(ABLY_EVENT_PLAYER_ANSWER_HAS_SELECTED, message);
  };

  onVideoClientJoined = (streamId) => {
    this.setState({streamId});

    if (this.channel) {
      const {game: {currentGame: {players}}} = this.props;
      // ably v2: connection.id is still the unique public connection identifier.
      const connectionId = AblyService.realtime.connection.id;
      const presenceData = {
        connectionId,
        streamId,
        gameId: this.gameId,
      };
      // ably v2: presence.enter(data) returns a Promise (the v1 callback is
      // dropped); presence.get() resolves to a PresenceMessage[] (a flat array,
      // not a PaginatedResult), so it is used directly.
      this.channel.presence.enter(presenceData)
        .then(() => this.channel.presence.get())
        .then((res = []) => {
          res.forEach((user) => {
            const player = players.find(pl => (pl.id === user.clientId));
            if (player) {
              player.streamId = user.data.streamId;
            }
          });
        })
        .catch(err => console.error('Error entering presence:', err));
    }
  };

  onVideoClientReJoined = (streamId) => {
    const {user: {profile: {id: userId}}, onPlayerRejoined} = this.props;
    this.onVideoClientJoined(streamId);
    onPlayerRejoined({userId, streamId});
  };

  onRemoteStreamAdded = (stream, streamId) => {
    const {subscribedStreams} = this.state;
    this.setState({subscribedStreams: {...subscribedStreams, [streamId]: stream}});
  };

  onRemoteStreamRemove = (stream, streamId) => {
    const {subscribedStreams} = this.state;
    const {onRemoteVideoUnmuted} = this.props;
    delete subscribedStreams[streamId];
    onRemoteVideoUnmuted({uid: streamId});
    this.setState({subscribedStreams: {...subscribedStreams}});
  };

  onPlayerOnlineMessage = (onlinePlayer) => {
    const {game: {currentGame: {players}}, onPlayerOnline} = this.props;
    const {clientId, data: {streamId}} = onlinePlayer;
    const player = players.find(pl => pl.id === clientId);

    if (player) {
      onPlayerOnline({...player, streamId});
    }
  };

  onPlayerOfflineMessage = (offlinePlayer) => {
    const {game: {currentGame: {players}}, onPlayerOffline} = this.props;
    const {clientId} = offlinePlayer;
    const player = players.find(pl => pl.id === clientId);
    if (player) {
      onPlayerOffline(player);
    }
  };

  onPlayerAnswerHasChecked = (data) => {
    const {onPlayerAnswerHasChecked} = this.props;
    const {isCorrect} = data;
    this.playSound(isCorrect ? this.correctSound : this.wrongSound);
    onPlayerAnswerHasChecked(data);
  };

  onJoinGame = (params) => {
    const {onPlayerJoin} = this.props;

    const {type} = params;
    const {user: {profile: {firstName, lastName}}} = this.props;

    if (type === 'audience') {
      this.chatChannel.publish(CHAT_EVENT, {
        type: 'info',
        sendAt: new Date(),
        message: `${firstName} ${lastName} has joined the game`,
      });
    }

    if (this.channel) {
      // ably v2: presence.get() returns a Promise resolving to a
      // PresenceMessage[] (a flat array), instead of the v1 (err, res) callback.
      return this.channel.presence.get()
        .then((res = []) => {
          const playersOnline = res.map(user => user.clientId);
          onPlayerJoin(params, playersOnline);
        })
        .catch((err) => {
          console.error('Failed to get presence on join:', err);
          onPlayerJoin(params);
        });
    }

    return onPlayerJoin(params);
  };

  onPlayerKickInit = (data) => {
    const {onPlayerKickInit, user: {profile: {id: userId}}} = this.props;
    onPlayerKickInit(data, userId);
  };

  onPlayerKicked = (data) => {
    const {onPlayerKicked, user: {profile: {id: userId}}} = this.props;
    onPlayerKicked(data, userId);
  };

  getPlayersOnline = () => {
    if (this.channel) {
      // ably v2: presence.get() returns a Promise resolving to a
      // PresenceMessage[] (a flat array), instead of the v1 (err, res) callback.
      this.channel.presence.get()
        .then((res = []) => {
          res.forEach(user => this.onPlayerOnlineMessage(user));
        })
        .catch(err => console.error('Failed to get online players:', err));
    }
  };

  initVideoService = async () => {
    const {user: {profile: {id: userId}}, onRemoteVideoMuted, onRemoteVideoUnmuted} = this.props;
    this.videoServiceNg.videoMutedListener = onRemoteVideoMuted;
    this.videoServiceNg.videoUnmutedListener = onRemoteVideoUnmuted;
    this.videoServiceNg.remoteStreamAddedListener = this.onRemoteStreamAdded;
    this.videoServiceNg.remoteStreamRemovedListener = this.onRemoteStreamRemove;
    const uid = await this.videoServiceNg.joinGame(userId, this.gameId);
    this.onVideoClientJoined(uid);
  };

  playSound = (sound) => {
    sound.play();
  };

  subscribeEvents = () => {
    const {
      onPlayerReadyMessage,
      onPlayerJoinMessage,
      onPlayerLeft,
      onGameStart,
      onGameRoundFinished,
      onGameRoundStarted,
      onGameQuestionNextMessage,
      onPlayerAnswerHasSelected,
      onInterstitialHasStarted,
      onInterstitialHasFinished,
      onGameHasFinished,
      onPlayerGiftSent,
      onTeamGetsPoints,
      onPlayerGetsPoints,
      onPlayerKickVoted,
      onRespondentChanged,
      onGameAdminChanged,
      user: {profile},
    } = this.props;

    const dispatcher = {
      [ABLY_EVENT_PLAYER_HAS_JOINED]: onPlayerJoinMessage,
      [ABLY_EVENT_PLAYER_HAS_LEFT]: onPlayerLeft,
      [ABLY_EVENT_PLAYER_READY]: onPlayerReadyMessage,
      [ABLY_EVENT_GAME_HAS_STARTED]: onGameStart,
      [ABLY_EVENT_GAME_ROUND_HAS_FINISHED]: onGameRoundFinished,
      [ABLY_EVENT_GAME_ROUND_HAS_STARTED]: onGameRoundStarted,
      [ABLY_EVENT_GAME_QUESTION_NEXT]: onGameQuestionNextMessage,
      [ABLY_GAME_ADMIN_HAS_CHANGED]: onGameAdminChanged,
      [ABLY_EVENT_PLAYER_ANSWER_HAS_CHECKED]: this.onPlayerAnswerHasChecked,
      [ABLY_EVENT_PLAYER_ANSWER_HAS_SELECTED]: onPlayerAnswerHasSelected,
      [ABLY_EVENT_GAME_HAS_FINISHED]: onGameHasFinished,
      [ABLY_EVENT_GAME_INTERSTITIAL_HAS_STARTED]: onInterstitialHasStarted,
      [ABLY_EVENT_GAME_INTERSTITIAL_HAS_FINISHED]: onInterstitialHasFinished,
      [ABLY_EVENT_GAME_TEAM_POINTS_INCREMENT]: onTeamGetsPoints,
      [ABLY_EVENT_GAME_PLAYER_POINTS_INCREMENT]: onPlayerGetsPoints,
      [ABLY_EVENT_PLAYER_SEND_GIFT_SUCCESS]: onPlayerGiftSent,
      [ABLY_EVENT_GAME_RESPONDENT_HAS_BEEN_CHANGED]: onRespondentChanged,
      [ABLY_EVENT_GAME_PLAYER_KICK_INIT]: this.onPlayerKickInit,
      [ABLY_EVENT_GAME_PLAYER_KICK_VOTED]: onPlayerKickVoted,
      [ABLY_EVENT_GAME_PLAYER_KICKED]: this.onPlayerKicked,
      [ABLY_ERROR]: this.logAblyError,
    };

    if (!this.channel) {
      return;
    }

    this.channel.presence.subscribe('enter', this.onPlayerOnlineMessage);
    this.channel.presence.subscribe('leave', this.onPlayerOfflineMessage);

    this.channel.subscribe((message) => {
      const {name, data} = message;
      const ablyMessageFn = dispatcher[name];
      if (isFunction(ablyMessageFn)) {
        ablyMessageFn(data, profile.id);
      }
    });
  };

  logAblyError = (error) => {
    const {dispatch} = this.props;
    const {data: {message}} = error;
    dispatch({type: ON_API_ERROR, error: message});
  };

  onChatToggle = (value) => {
    const {toggleChatPanel} = this.props;
    toggleChatPanel(value);
  }

  onLeaderboardToggle = (value) => {
    const {toggleLeaderboard} = this.props;
    toggleLeaderboard(value);
  }

  onTreasureChestToggle = (value) => {
    const {toggleTreasureChest} = this.props;
    toggleTreasureChest(value);
  }

  redeemToast() {
    const {treasures: {redeemSuccess}, dispatch} = this.props;

    if (redeemSuccess) {
      toast.success(treasureRedeemedMessage);
      dispatch(redeemClean());
    }
  }

  _renderJoinGame = () => {
    const {
      templateConfig: {primaryColor, secondaryColor},
      gameConfig: {isAudienceAllowed},
      game: {
        branchIoLink = '',
        currentTeamDefinition, fetchingCurrentTeamDefinition,
        currentGame: {isAudienceAllowed: isGameAudienceAllowed},
        currentGame,
        isLandingShowed,
      },
      saveUserDefinitions,
      confirmMedia,
      fetchTeamDefinition,
      user: {profile},
      onPlayerEnteredGameByDirectLink,
      fetchGameTeamDefinition,
      fetchMobileBranchIoLink,
      ableToSendGiftsForFree,
    } = this.props;

    return (
      <JoinGameByDirectLink
        branchIoLink={branchIoLink}
        ableToSendGiftsForFree={ableToSendGiftsForFree}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        isAudienceAllowed={isAudienceAllowed}
        currentTeamDefinition={currentTeamDefinition}
        fetchingCurrentTeamDefinition={fetchingCurrentTeamDefinition}
        isLandingShowed={isLandingShowed}
        saveUserDefinitions={saveUserDefinitions}
        confirmMedia={confirmMedia}
        onPlayerJoin={this.onJoinGame}
        fetchTeamDefinition={fetchTeamDefinition}
        profile={profile}
        isGameAudienceAllowed={isGameAudienceAllowed}
        currentGame={currentGame}
        gameId={this.gameId}
        onPlayerEnteredGameByDirectLink={onPlayerEnteredGameByDirectLink}
        fetchGameTeamDefinition={fetchGameTeamDefinition}
        fetchMobileBranchIoLink={fetchMobileBranchIoLink}
      />);
  }

  _renderGame = () => {
    const channelId = `game/${this.gameId}/chat`;
    const {user: {profile}, game, application, toggleLeaderboard, toggleTreasureChest,
      panel: {isChatPanelOpened, isTreasurePanelOpened, isLeaderboardPanelOpened}} = this.props;
    const {currentGame: {name, audience = [], players = [], gifts = {}}} = game;
    const player = players.find(pl => (pl.id === profile.id));
    const isAudience = audience.some(pl => pl.id === profile.id);
    const {streamId, subscribedStreams} = this.state;
    const teamName = (player && player.team) || '';

    return (
      <DocumentTitle title={`${application.name} - ${name}`}>
        <Wrapper {...this.props}>
          <div className="row cell cell_full">
            <Game
              {...this.props}
              gameId={this.gameId}
              streamId={streamId}
              subscribedStreams={subscribedStreams}
              videoService={this.videoServiceNg}
              onAnswerSelected={this.onAnswerSelected}
              onChatToggle={this.onChatToggle}
              gifts={gifts}
              reInitializeVideo={this.reInitializeVideo}
            />
          </div>
          <div className="cell cell_auto">
            <Chat
              isDisplayed={isChatPanelOpened}
              onChatToggle={this.onChatToggle}
              isAudience={isAudience}
              channelId={channelId}
              user={profile}
              team={teamName}
            />
            <TreasureChestPanel
              isDisplayed={isTreasurePanelOpened}
              onClose={window.innerWidth < 880 ? toggleTreasureChest : false}
            />
            <LeaderboardPanel
              isDisplayed={isLeaderboardPanelOpened}
              onClose={window.innerWidth < 880 ? toggleLeaderboard : false}
            />
          </div>
        </Wrapper>
      </DocumentTitle>
    );
  };

  render() {
    const {displayJoinForm} = this.state;
    return (displayJoinForm ? this._renderJoinGame() : this._renderGame());
  }
}

const mapDispatchToProps = dispatch => (
  {
    dispatch,
    helpToggle: params => dispatch(userActions.setHelpStatus(params)),
    ...bindActionCreators({
      ...playerActions,
      ...gameActions,
      ...userActions,
      ...appActions,
      ...panelActions,
    }, dispatch),
  }
);

const mapStateToProps = (state, ownProps) => {
  const {
    user, game, error, application: {details}, panelNav, treasures,
  } = state;
  const {ableToSendGiftsForFree = false} = details || {};

  return (
    {
      ...ownProps,
      game,
      application: details,
      panel: panelNav,
      user,
      gameConfig: gameConfigSelector(state),
      templateConfig: templateConfigSelector(state),
      ableToSendGiftsForFree,
      error,
      treasures,
    }
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(GameContainer);
